import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Headphones, Target, MessageCircle, Flame } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1759984782106-4b56d0aa05b8?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
const WORDS = [["¡Hola!", "es"], ["Bonjour", "fr"], ["Hallo", "de"], ["こんにちは", "ja"], ["Ciao", "it"]];
const FEATURES = [
  { icon: Target, title: "Assessments that unlock", text: "Score 70% to open the next topic. Prove it, then move on." },
  { icon: Headphones, title: "Hear every word", text: "Tap any word or sentence to hear it, slow it down, then listen and choose." },
  { icon: MessageCircle, title: "A patient AI tutor", text: "Chat at your level. Lumi corrects you gently and explains in English." },
  { icon: Flame, title: "Streaks and XP", text: "Small daily wins add up. Keep your flame alive at your own pace." },
];

export default function Landing() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] overflow-hidden">
      <header className="max-w-6xl mx-auto px-5 h-20 flex items-center">
        <span className="h-9 w-9 rounded-xl bg-[hsl(var(--primary))] text-white grid place-items-center font-display font-extrabold">L</span>
        <span className="ml-2 font-display font-extrabold text-xl">LinguaPath</span>
        <Link to="/login" data-testid="landing-login-link" className="ml-auto text-sm font-bold text-slate-700 hover:text-slate-900 px-4 py-2">Log in</Link>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-8 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 animate-fade-up">
          <div className="flex flex-wrap gap-2 mb-6">
            {WORDS.map(([w, c], i) => (
              <span key={c} className="word-pill" style={{ animationDelay: `${i * 80}ms` }}>{w}</span>
            ))}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Learn a language <span className="text-[hsl(var(--primary))]">at your own pace</span>, one small win at a time.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl">
            Spanish, French, German, Japanese and Italian. Start from zero or polish your fluency across Basic, Intermediate and Advanced levels with AI lessons, games and assessments.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="landing-get-started-button" className="btn-primary !px-7">
              Start learning free <ArrowRight size={18} />
            </Link>
            <Link to="/login" data-testid="landing-have-account-button" className="btn-ghost">I have an account</Link>
          </div>
        </div>
        <div className="lg:col-span-6 relative">
          <div className="absolute -top-8 -right-8 h-72 w-72 rounded-full bg-[hsl(var(--secondary-light))]" />
          <div className="absolute -bottom-10 -left-6 h-40 w-40 rounded-[2rem] bg-[hsl(var(--primary-light))] rotate-12" />
          <img src={HERO} alt="Learner studying" className="relative rounded-[2rem] w-full h-[460px] object-cover shadow-2xl" />
          <div className="absolute left-4 bottom-6 sm:-left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 animate-float">
            <span className="h-11 w-11 rounded-xl bg-[hsl(var(--secondary-light))] grid place-items-center"><Flame className="text-[hsl(var(--secondary))] fill-orange-300" /></span>
            <div><p className="font-display font-extrabold text-slate-900">12 day streak</p><p className="text-xs text-slate-500">Topic passed: 90%</p></div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {FEATURES.map((f) => (
            <div key={f.title}>
              <f.icon className="text-[hsl(var(--primary))] mb-4" size={28} />
              <h3 className="font-display font-bold text-lg text-slate-900">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-1">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-16 grid md:grid-cols-3 gap-6">
        {[["Basic", "No knowledge needed. Greetings, numbers, food and family."], ["Intermediate", "Travel, the past tense, work and real conversations."], ["Advanced", "Opinions, idioms, news, culture and formal language."]].map(([t, d], i) => (
          <div key={t} className="rounded-3xl border border-slate-200 bg-white p-7 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]">
            <span className="caption">Level {i + 1}</span>
            <h3 className="font-display text-2xl font-extrabold text-slate-900 mt-1">{t}</h3>
            <p className="text-slate-600 mt-2 text-sm">{d}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
